import QRCode from 'qrcode';
import qs from 'querystring';

import { downloadUrl, MINI_INFO } from '@/common/const';
import { getWxSchemaUrl } from '@/services';
import { getApolloConfig, queryPackageWebPage } from '@/services/activity';

import { isJoJoBrowser, isWXBrowser, openMiniApp, qsParse, reportError } from './index';
import { sensElementView } from './sensors';
import { reportCustomError } from './sentry';

/**
 * Buffer 转换为 Base64 图片 URL，并验证图片可用性
 * @param buffer - 图片 Buffer
 * @returns 有效的 Base64 URL 或空字符串
 */
const bufferToBase64 = async (base64Data: string): Promise<string> => {
  try {
    const mimeType = 'image/jpeg';
    const base64Url = `data:${mimeType};base64,${base64Data}`;

    // 第四步：验证 Base64 图片是否可正常加载（异步）
    await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(true); // 加载成功 = 图片有效
      img.onerror = () => reject(new Error('Base64 图片加载失败')); // 加载失败 = 图片无效
      img.src = base64Url;
    });

    return base64Url;
  } catch (error) {
    reportCustomError({
      name: 'Buffer 转 Base64 失败',
      message: 'Buffer 转 Base64 失败'
    });
    return '';
  }
};

// 生成分享二维码/小程序码
export const generateShareMiniQrcode = async ({
  miniAppId,
  miniProgramCode
}: {
  miniAppId: string;
  miniProgramCode: string;
}) => {
  // 初始化默认值，减少重复赋值
  const result = {
    miniAppId: 'h5', // 默认h5方式
    qrCodeImg: ''
  };

  // 仅当存在小程序码时处理
  if (miniProgramCode) {
    const base64Url = await bufferToBase64(miniProgramCode);
    // 成功转换Base64才使用小程序码方式
    if (base64Url) {
      result.miniAppId = miniAppId;
      result.qrCodeImg = base64Url;
    }
  }

  return result;
};

/**
 * 生成转介绍分享链接
 * @param {*} params
 */
export const generateShareUrl = async (params: {
  useShortUrl: boolean;
  scanCodeJumpUrl: string;
  miniAppId?: string;
  channel?: string;
  channelNo?: string;
  channelSuffix?: string;
  inviteCode?: string;
  platform?: number;
  shareHub?: string;
}) => {
  const {
    scanCodeJumpUrl = '',
    miniAppId = 'h5',
    channel,
    channelNo,
    channelSuffix,
    useShortUrl,
    inviteCode,
    platform,
    shareHub
  } = params;
  let shareUrl = '';
  if (['wx5bb273ef4d60d29c', 'wx05f8e6e81c6f2dca'].includes(miniAppId)) {
    const miniAppShareUrlsRes = await getApolloConfig<any>({
      key: 'campaignPageOpenPlatformMiniPrograms'
    });
    try {
      if (miniAppShareUrlsRes?.data) {
        const miniAppShareUrlsData = JSON.parse(miniAppShareUrlsRes?.data);
        if (Array.isArray(miniAppShareUrlsData)) {
          const miniAppShareUrl = miniAppShareUrlsData?.find(
            (i: any) => i.openPlatformAppId === miniAppId
          );
          if (miniAppShareUrl) {
            shareUrl = `${miniAppShareUrl.miniProgramIntro}&channel=${channel}${
              channel ? channelSuffix : ''
            }&inviteCode=${inviteCode}&channelNo=${channelNo}`;
          }
        }
      }
    } catch (error) {
      console.log(error);
    }
  }
  // SP112 动态小程序跳转
  if (miniAppId === 'dynamic_mini_program' && scanCodeJumpUrl) {
    // SP119 新增存储scanCodeJumpUrl
    __store.scanCodeJumpUrl = scanCodeJumpUrl;
    shareUrl = `${scanCodeJumpUrl}&channel=${channel}${
      channel ? channelSuffix : ''
    }&inviteCode=${inviteCode}&channelNo=${channelNo}`;
    const url = scanCodeJumpUrl?.split('?')?.[1] || '';
    const urlObj = qs.parse(url);
    const { markId, sceneLabel } = urlObj || {};
    const query = qsParse();
    // 上报h5海报页生成了 打开小程序去领课的二维码 元素浏览事件
    sensElementView({
      $element_name: '运营活动海报页_小程序二维码浏览',
      activity_id: query?.activityId || '',
      activity_label: '转介绍',
      topic_id: markId || '',
      topic_name: sceneLabel || ''
    });
  }
  if (!shareUrl) {
    shareUrl = useShortUrl
      ? scanCodeJumpUrl
      : `${scanCodeJumpUrl}&channel=${channel}${
          channel ? channelSuffix : ''
        }&channelNo=${channelNo}&appId=${
          ['h5', null, undefined].includes(miniAppId) ? '' : miniAppId
        }&parentPlatform=${platform || ''}&shareHub=${shareHub || ''}`;
  }

  return shareUrl;
};

// 定义工具函数需要的参数类型（根据实际场景补充）
interface GenerateQrCodeParams {
  miniAppId: string;
  miniProgramCode: string;
  useShortUrl: boolean;
  scanCodeJumpUrl: string;
  scanCodeJumpOriginalUrl: string;
  channel: string;
  channelNo: string;
  channelSuffix: string;
  inviteCode: string;
  platform?: number;
  shareHub?: string;
  imgType?: 'image/jpeg' | 'image/png' | 'image/webp'; // 二维码图片类型，默认 'image/jpeg'
}

/**
 * 统一生成二维码图片（处理小程序码和普通链接二维码的逻辑）
 * @param params 生成二维码所需的参数
 * @returns 二维码图片的 base64 或 url
 */
export async function generateQrCodeImage(params: GenerateQrCodeParams): Promise<{
  qrCodeImg: string;
  posterImgUrl: string;
  miniAppId: string;
}> {
  const {
    miniAppId,
    miniProgramCode,
    useShortUrl,
    scanCodeJumpUrl,
    channel,
    channelNo,
    channelSuffix,
    inviteCode,
    scanCodeJumpOriginalUrl,
    platform,
    shareHub,
    imgType = 'image/jpeg'
  } = params;

  let useMiniAppId = miniAppId;
  let posterImgUrl = scanCodeJumpOriginalUrl;

  // 1. 优先尝试生成小程序码（如果类型匹配）
  if (miniAppId === 'mini_program_code') {
    try {
      const miniQrResult = await generateShareMiniQrcode({
        miniAppId,
        miniProgramCode
      });
      useMiniAppId = miniQrResult.miniAppId;
      // 如果小程序码生成成功，直接返回
      if (miniQrResult.qrCodeImg) {
        return {
          qrCodeImg: miniQrResult.qrCodeImg,
          posterImgUrl,
          miniAppId: useMiniAppId
        };
      }
    } catch (error) {
      console.warn('小程序码生成失败，降级生成普通二维码:', error);
    }
  }

  // 2. 小程序码生成失败或不适用时，生成普通链接二维码
  try {
    // 生成分享链接
    const shareUrl = await generateShareUrl({
      useShortUrl,
      scanCodeJumpUrl,
      channel,
      channelNo,
      channelSuffix,
      // 使用最终确定的miniAppId
      miniAppId: useMiniAppId,
      platform,
      shareHub,
      inviteCode
    });
    posterImgUrl = shareUrl;
    // 将链接转换为二维码图片
    const qrCodeBase64 = await QRCode.toDataURL(shareUrl, {
      margin: 1,
      scale: 2,
      type: imgType,
      errorCorrectionLevel: 'L'
    });

    return {
      qrCodeImg: qrCodeBase64,
      posterImgUrl,
      miniAppId: useMiniAppId
    };
  } catch (error) {
    console.error('普通二维码生成失败:', error);
    throw new Error('二维码生成失败'); // 抛出错误让上层处理
  }
}

export function openMiniAppBySchema({
  url,
  needLogin,
  miniProgram
}: {
  url: string;
  needLogin?: number;
  miniProgram?: string;
}) {
  // 其他方式 则通过schema方式跳转
  const nd = needLogin === 0 ? 0 : 1;
  const str = `type=webview&needLogin=${nd}&linkUrl=${encodeURIComponent(url)}`;
  getWxSchemaUrl<{ schemaUrl: string }>({
    miniProgram: miniProgram || MINI_INFO.miniProgram, // jojo阅读小程序
    query: str
  }).then(({ resultCode, data }) => {
    if (resultCode === 200 && data) {
      window.location.href = data?.schemaUrl;
    }
  });
}

type MiniPorgramType = {
  name: string;
  miniAppId: string;
  miniAppUrl: string;
  userName: string;
  miniProgramType: string;
  miniProgram: string;
};

// 打开方式为小程序时
export async function openLinkByMiniWay({
  miniAppJumpUrl,
  miniAppId
}: {
  miniAppJumpUrl: string;
  miniAppId: string;
}) {
  try {
    if (miniAppJumpUrl && miniAppId) {
      // 小程序id和小程序同时存在
      if (isWXBrowser()) {
        // 如果在微信环境包括小程序内，则直接跳转miniAppJumpUrl
        window.location.href = miniAppJumpUrl;
        return '';
      }
      const resp = await getApolloConfig<string>({ key: 'boom.activity.webpage.mini.app' });
      let curMini;
      if (resp && resp.data) {
        // TODO
        const data = JSON.parse(resp.data) as MiniPorgramType[];
        curMini = data?.filter((item) => item.miniAppId === miniAppId)?.[0] ?? [];
      }
      if (isJoJoBrowser()) {
        // 如果在APP内，则通过bridge跳转, 兼容老方法
        openMiniApp(miniAppJumpUrl, {
          needLogin: 0,
          userName: curMini?.userName,
          miniProgramType: curMini?.miniProgramType
        });
        return '';
      }
      // 其他方式 则通过schema方式跳转
      openMiniAppBySchema({ url: miniAppJumpUrl, needLogin: 0, miniProgram: curMini?.miniProgram });
      return '';
    }
  } catch (error) {
    if (typeof reportError === 'function') {
      reportError(error);
    }
  }
  return '';
}

export function labelName(label = '') {
  const obj: any = {
    iftw_zdqxk: '组队抢先看',
    iftw_xsz1d4: '限时赠一得四',
    iftw_z1d1: '赠一得一'
  };

  return obj[label] || label;
}

export const sensInfo: any = {
  activity_label: '',
  material_custom: '',
  $utm_content: '',
  material_ids: [],
  sku_id: '',
  activity_id: ''
};

export const CALLBACK_URL = 'callback_url';
export const INTRO_URL = 'intro_url';

export async function getWebPageInfo(payload: { webPageId: string; activityCode: string }) {
  let introUrl = sessionStorage.getItem(INTRO_URL);
  let crn = 0;
  const { code, data } = await queryPackageWebPage<any[]>({
    ...payload,
    _t: Date.now()
  });
  if (code === 'SUCCESS' && data) {
    const giftsData = data?.find((item) => item?.componentType === 'RECOMMEND_GIFT') || {};
    introUrl = giftsData?.commonExtVo?.introUrl || introUrl;
    crn = giftsData?.dynamicData?.canReceiveNum;
  }
  return { introUrl, crn };
}

export async function toAppDownloadPage(replace?: boolean) {
  let hash = '';
  try {
    const { resultCode, data } = await getApolloConfig<string>({
      key: 'boom.transfer.hash'
    });
    if (resultCode === 200) {
      const res = JSON.parse(data || '[]');
      const len = res.length;
      if (len) {
        const now = new Date();
        const hour = now.getHours();
        if (len > 23) {
          hash = res[hour];
        } else {
          // 根据当前小时数计算对应的数组索引
          const index = Math.floor((hour * len) / 24);
          hash = res[index];
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
  if (!hash) {
    hash = 'tr2a';
  }
  const downPage = downloadUrl.replace('${hash}', hash);
  if (replace) {
    window.location.replace(downPage);
  } else {
    window.location.href = downPage;
  }
}
